import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Text } from '../../cdl/Text/Text';
import { TextButton } from '../buttons/TextButton';
import { translate } from '../helpers/translate.helper';
import { theme } from '../ui/theme';

const Container = styled.div`
    background-color: ${theme.colors.background.inset};
    padding: ${theme.space[16]} ${theme.space[24]};
    display: flex;
    gap: ${theme.space[8]};
    flex-direction: column;
    align-items: flex-start;
`;

interface TextWrapperProps {
    shouldTruncate: boolean;
}

const TextWrapper = styled.div<TextWrapperProps>`
    width: 100%;
    overflow: hidden;
    text-overflow: ${({ shouldTruncate }) => (shouldTruncate ? 'ellipsis' : 'clip')};
    white-space: ${({ shouldTruncate }) => (shouldTruncate ? 'nowrap' : 'pre-line')};
`;

interface OrderCreationCommentNewProps {
    text: string;
}

export const CreationComment = ({ text }: OrderCreationCommentNewProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);

    const textWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (text.includes('\n')) {
            setIsTruncated(true);
            return;
        }

        if (isExpanded) {
            return;
        }

        const element = textWrapperRef.current;
        if (!element) return;

        const checkTruncation = () => {
            setIsTruncated(element.scrollWidth > element.clientWidth);
        };

        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(element);
        checkTruncation();

        return () => resizeObserver.disconnect();
    }, [text, isExpanded]);

    return (
        <Container>
            <Text variant="subtitle1">{translate('creationComment.headline')}</Text>
            <TextWrapper ref={textWrapperRef} shouldTruncate={!isExpanded}>
                <Text variant="small" color="foreground.muted">
                    {text}
                </Text>
            </TextWrapper>
            {isTruncated ? (
                <TextButton type="button" onClick={() => setIsExpanded(!isExpanded)}>
                    {isExpanded ? translate('creationComment.showLess') : translate('creationComment.showMore')}
                </TextButton>
            ) : null}
        </Container>
    );
};
